import { FC, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import isFunction from 'lodash/isFunction';
import { THEME_BLACK, THEME_WHITE, themeColor } from '../../../styles/basics/colors';
import requireFunctionIfFlagExists from '../../utils/custom-proptypes/requireFunctionIfFlagExists';
import { usePreviousProps } from '../../../hooks/usePreviousProps';

interface ModalProps {
    visible: boolean;
}

export const StModalContainer = styled.div<ModalProps>`
    background: rgba(225, 225, 225, 0.85);
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1000; /* _000_layers.scss: page-overlay */
    display: ${props => (props.visible ? 'block' : 'none')};
`;

interface ModalWrapProps {
    width: number | undefined;
}

const StModalWrap = styled.div.attrs({ 'data-displayname': 'ModalLayout' })<ModalWrapProps>`
    background-color: ${themeColor(THEME_WHITE)};
    border: 1px solid #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.26);
    line-height: 26px;
    position: fixed;
    margin: 0 auto;
    left: 0;
    right: 0;
    top: 10%;
    width: ${props => (props.width ? `${props.width}px` : '600px')};
    z-index: 1000;
    padding: 35px;
`;

const StCloseButton = styled.a`
    position: absolute;
    right: 0;
    top: 0;
    i {
        color: ${themeColor(THEME_BLACK)};
    }
`;

StCloseButton.displayName = 'ModalLayout__StCloseButton';

const StHeaderContainer = styled.div`
    position: relative;
    margin-bottom: 20px;
`;

export const MODAL_ROOT_CLASS = 'modal-root';
type Props = {
    visible: boolean;
    header?: ReactNode;
    content?: ReactNode;
    className?: string;
    footer?: ReactNode;
    width?: number;
    destroyOnClose?: boolean;
    style?: object;
    onClose?: () => void;
    closeOnEsc?: boolean;
    closable?: boolean;
};

const ModalLayout: FC<Props> = ({
    header,
    content,
    footer,
    width,
    visible,
    closable = true,
    style,
    className,
    onClose = requireFunctionIfFlagExists('closable') && requireFunctionIfFlagExists('closeOnEsc'),
    ...props
}) => {
    const modalWrap = useRef(null);

    const [isMounted, setIsMounted] = useState(visible);

    const close = useCallback(() => {
        if (typeof onClose !== 'undefined' && isFunction(onClose)) {
            onClose(props, onClose, ModalLayout);
        }
    }, [onClose, props]);

    const handleKeyUp = useCallback(
        (e: { key: string }) => {
            if (e.key === 'Escape') {
                close();
            }
        },
        [close]
    );

    const preVisibleRef = usePreviousProps(visible);

    useEffect(() => {
        document.addEventListener('keyup', handleKeyUp);

        if (!preVisibleRef && visible && !isMounted) {
            setIsMounted(true);
        }
        return () => {
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [handleKeyUp, isMounted, preVisibleRef, visible]);

    function buildModalDialog() {
        const closeButton = closable ? (
            <StCloseButton onClick={close}>
                <i className="material-icons">clear</i>
            </StCloseButton>
        ) : null;

        const headerWrapper = (
            <StHeaderContainer>
                {closeButton}
                {header}
            </StHeaderContainer>
        );

        return (
            <StModalContainer visible={visible}>
                <StModalWrap className={className} style={style} ref={modalWrap} width={width}>
                    {headerWrapper}
                    {content}
                    {footer}
                </StModalWrap>
            </StModalContainer>
        );
    }

    if (typeof isMounted !== 'undefined' && !isMounted) return null;
    const modal = buildModalDialog();
    return ReactDOM.createPortal(modal, document.body);
};

export default ModalLayout;
